/**
 * 이상한 방 - 오브젝트 뷰 (RoomState → Three.js 씬)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3절(좌표) · 4.2절(대칭 물체의 무변화 회전)
 *
 * 이 모듈은 렌더 레이어이므로 **Three.js 객체의 제자리 변경을 허용한다**(코딩 규칙의 명시적 예외).
 * 대신 코어 상태(`RoomState`·`ObjectInstance`)는 절대 건드리지 않는다 - 읽기 전용 입력이다.
 *
 * ## 역할 분담
 *
 * 형상은 `meshFactory.ts`가 만든다(GLB 또는 절차적 폴백). 이 파일이 하는 일은 셋이다.
 *  1. 상태 → 씬의 **차분 동기화** (추가·제거·갱신만. 전체 재생성 금지)
 *  2. 그리드 좌표 → 월드 변환 (코어 `grid.ts`와 같은 함수를 쓴다)
 *  3. **방향 표식**을 몸통에 붙이기
 *
 * ## 방향 표식이 형상과 별개로 남아 있는 이유
 *
 * 4.2절 부수 요건: "렌더링에서도 비대칭 소품의 방향이 식별 가능해야 한다."
 * 이제 몸통은 소파·책장 같은 실제 형상이라 방향이 형태만으로도 대체로 읽힌다.
 * 그런데 몸통은 **GLB일 수도 있고 절차적 폴백일 수도 있다.** 즉 몸통의 실제 대칭성은
 * 에셋 파일에 달려 있고, 코드가 보증할 수 없다. 만약 어떤 GLB가 실제로는 4방향 대칭인데
 * 카탈로그가 `rotSymmetry: 1`이라고 선언해 두면 서버가 승인한 회전이 화면에서 무변화가 된다 -
 * 술래가 원리적으로 찾을 수 없는 정답이며 원칙 2의 정면 위반이다.
 *
 * 표식은 그 구멍을 코드로 막는다. 개수와 배치를 `rotSymmetry`에서 **역산**하므로
 * (`computeMarkerPlacements()`), 몸통이 무엇이든 화면상 구별되는 회전 상태 수가
 * 정확히 `4 / rotSymmetry`가 되는 것이 보장된다.
 * 대신 실제 형상이 방향을 이미 말해 주는 만큼, 표식은 **밑동을 두르는 얇은 띠**로 낮췄다.
 */

import { Color, Group, Line, Mesh, type Material, type Object3D } from 'three'

import { cellToWorld, rotatedFootprint } from '../core/grid'
import type { Catalog } from '../core/room'
import {
  CELL_SIZE_METERS,
  ROT_STEP_COUNT,
  type CatalogEntry,
  type Cell3,
  type InstanceId,
  type ObjectInstance,
  type PaletteIndex,
  type RoomState,
  type Rot,
  type RotSymmetry,
} from '../core/types'
import {
  applyBodyColor,
  createObjectBody,
  createSharedMesh,
  getSlotMaterial,
  isHitCollider,
  isSharedResource,
  measureBodyExtent,
} from './meshFactory'
import type { SceneCtx } from './scene'

/* --- 씬 구조 규약 ------------------------------------------------------------- */

/**
 * 오브젝트 뷰를 담는 컨테이너의 이름.
 * 모듈 전역 캐시 대신 씬에서 이름으로 찾는 이유: 씬이 여러 개(테스트·정답 방 로컬 씬 → 11.3절)
 * 존재할 수 있는데, 모듈 전역 상태를 두면 두 씬이 서로의 뷰를 지우게 된다.
 */
const OBJECT_VIEW_ROOT_NAME = 'object-views'

const USER_DATA_INSTANCE_ID = 'instanceId'
const USER_DATA_CATALOG_ID = 'catalogId'
/** 마지막으로 화면에 반영한 `ObjectInstance` 참조. diff 스킵 판정에 쓴다. */
const USER_DATA_INSTANCE = 'instance'
const USER_DATA_ROLE = 'role'

const ROLE_BODY = 'body'
const ROLE_MARKER = 'marker'

/* --- 형상 상수 ---------------------------------------------------------------- */

// 회전 단계 수(표식 방향 계산의 모듈러 기준)는 `core/types.ROT_STEP_COUNT` 하나다 - 사본을 들지 않는다(P1 정리).
const QUARTER_TURN_RADIANS = Math.PI / 2

/** 표식 두께(셀 크기 대비). 최소 소품(0.25m)에서도 실루엣에 걸리도록 잡은 값. */
const MARKER_THICKNESS_RATIO = 0.2
/** 표식이 덮는 가로 폭(해당 면의 가로 대비). 면을 다 덮으면 방향이 아니라 색이 되어 버린다. */
const MARKER_FACE_RATIO = 0.4
/** 표식 띠의 높이(몸통 높이 대비). 형상이 방향을 이미 말하므로 예전보다 낮게 두른다. */
const MARKER_BAND_RATIO = 0.18
/** 띠가 바닥에서 떠 있는 정도(몸통 높이 대비). 바닥에 딱 붙으면 그림자에 먹힌다. */
const MARKER_BAND_LIFT_RATIO = 0.06

/**
 * 이 셀 수 이상이면 "큰 가구"로 보고 그림자를 드리운다(1칸 = 0.25m이므로 4칸 = 1m).
 * 소품 400개가 전부 그림자를 만들면 섀도맵이 그 수만큼 갱신돼 프레임이 무너지고,
 * 무엇보다 작은 소품의 그림자는 화면을 지저분하게 만들어 **변화 찾기를 방해한다.**
 * 대신 작은 소품도 그림자를 **받기는** 한다 - 받지 않으면 공중에 뜬 것처럼 보인다.
 */
const SHADOW_CASTER_MIN_CELLS = 4

/** 셀 중심 기준 보정에 쓰는 값. `cellToWorld`가 '한 칸짜리 중심'을 주기 때문에 필요하다. */
const SINGLE_CELL = 1
const HALF = 2

/* --- 순수 계산 (gridHelper의 고스트가 그대로 재사용한다) ------------------------ */

/** 각 축의 실제 크기(미터). 셀 개수 × 셀 크기 - 축척 곱셈은 반드시 이 함수를 거친다. */
export function footprintToMeters(footprint: Cell3): { x: number; y: number; z: number } {
  return {
    x: footprint.x * CELL_SIZE_METERS,
    y: footprint.y * CELL_SIZE_METERS,
    z: footprint.z * CELL_SIZE_METERS,
  }
}

/** `rot`(0~3) → Y축 라디안. 연속 각도를 만들 수 없도록 입력 타입 자체가 이산값이다(4절). */
export function rotToRadians(rot: Rot): number {
  return rot * QUARTER_TURN_RADIANS
}

/**
 * 오브젝트 박스의 **중심** 월드 좌표.
 *
 * `cell`은 점유 박스의 최소 코너다(grid.ts `occupiedCells`의 규약).
 * `cellToWorld`는 한 칸짜리 중심을 주므로, 여러 칸을 차지하면 (칸수-1)/2 칸만큼 더 밀어야
 * 박스 중심이 맞는다. 이 보정을 빼먹으면 큰 가구일수록 점점 어긋난다.
 *
 * 회전은 최소 코너 기준이지만 축 정렬 박스이므로, **회전된 크기**로 중심을 잡으면
 * 결과적으로 메시를 중심에서 90° 돌린 것과 점유 영역이 정확히 일치한다.
 */
export function computeObjectCenterWorld(
  cell: Cell3,
  entry: CatalogEntry,
  rot: Rot,
): { x: number; y: number; z: number } {
  const size = rotatedFootprint(entry.footprint, rot)
  const origin = cellToWorld(cell)

  return {
    x: origin.x + ((size.x - SINGLE_CELL) / HALF) * CELL_SIZE_METERS,
    y: origin.y + ((size.y - SINGLE_CELL) / HALF) * CELL_SIZE_METERS,
    z: origin.z + ((size.z - SINGLE_CELL) / HALF) * CELL_SIZE_METERS,
  }
}

/** 방향 표식 하나의 배치 정보. 로컬 좌표(회전 전) 기준, 단위는 미터. */
export type MarkerPlacement = Readonly<{
  offset: Readonly<{ x: number; y: number; z: number }>
  size: Readonly<{ x: number; y: number; z: number }>
}>

/** 표식이 붙는 면의 방향. 인덱스가 곧 90° 회전 단계다(0 = 정면 +z). */
const MARKER_DIRECTIONS: readonly Readonly<{ x: number; z: number }>[] = [
  { x: 0, z: 1 },
  { x: 1, z: 0 },
  { x: 0, z: -1 },
  { x: -1, z: 0 },
]

/**
 * 방향 표식의 배치를 `rotSymmetry`에서 역산한다.
 *
 * 규칙은 한 줄이다: **표식 개수 = `rotSymmetry`, 배치 간격 = `4 / rotSymmetry` 단계.**
 *  - 1 → 정면 한 장. 네 방향이 전부 다르게 보인다
 *  - 2 → 앞뒤 두 장. 180° 회전에 대해 그림이 자기 자신으로 되돌아온다
 *  - 4 → 네 면 전부. 어떤 회전에도 그림이 바뀌지 않는다
 *
 * 즉 표식 배열의 회전 대칭 차수가 정확히 `rotSymmetry`가 되고,
 * 화면상 구별되는 회전 상태 수는 `4 / rotSymmetry`가 된다 -
 * 이는 verbs.ts `isRotationVisible()`의 주기와 **같은 식**이다(4.2절).
 * 서버가 거부하는 회전은 화면에서도 무변화이고, 서버가 승인하는 회전은 화면에서도 반드시 달라진다.
 *
 * (`rotSymmetry: 4`에 표식 네 장을 붙이는 것이 낭비처럼 보이지만 낭비가 아니다.
 *  표식 유무로 "이 소품은 돌릴 수 있다"가 새어 나가지 않게 하고,
 *  원형 러그가 "돌려도 아무 일도 안 일어난다"는 것을 화면으로 증명해 준다.)
 */
export function computeMarkerPlacements(
  footprint: Cell3,
  rotSymmetry: RotSymmetry,
): readonly MarkerPlacement[] {
  const body = footprintToMeters(footprint)
  const thickness = CELL_SIZE_METERS * MARKER_THICKNESS_RATIO
  const stride = ROT_STEP_COUNT / rotSymmetry
  const bandHeight = body.y * MARKER_BAND_RATIO
  // 밑동에 두른다. 몸통 중앙에 판을 붙이면 실제 가구 형상 위에 이물질처럼 얹힌다.
  const bandCenterY = -body.y / HALF + bandHeight / HALF + body.y * MARKER_BAND_LIFT_RATIO

  const placements: MarkerPlacement[] = []

  for (let index = 0; index < rotSymmetry; index += 1) {
    const direction = MARKER_DIRECTIONS[(index * stride) % ROT_STEP_COUNT]
    if (direction === undefined) {
      // 도달 불가(모듈러 결과는 항상 0~3). 조용히 넘기면 표식 없는 비대칭 소품이 생기므로 시끄럽게 실패한다.
      throw new RangeError(`방향 표식 인덱스 계산 오류: rotSymmetry=${rotSymmetry}, index=${index}`)
    }

    const isFacingZ = direction.z !== 0
    const faceWidth = isFacingZ ? body.x : body.z
    // 면에 **딱 붙이되 밖으로는 나가지 않게** 반 두께만큼 안으로 당긴다.
    // 삐져나가면 "보이는 부피 ≠ 점유 셀"이 되어 3.1절의 셀 점유 규칙이 화면과 어긋난다.
    const faceOffset = (isFacingZ ? body.z : body.x) / HALF - thickness / HALF
    const plateWidth = faceWidth * MARKER_FACE_RATIO

    placements.push({
      offset: { x: direction.x * faceOffset, y: bandCenterY, z: direction.z * faceOffset },
      size: {
        x: isFacingZ ? plateWidth : thickness,
        y: bandHeight,
        z: isFacingZ ? thickness : plateWidth,
      },
    })
  }

  return placements
}

/* --- 메시 생성 ---------------------------------------------------------------- */

/**
 * 오브젝트 하나를 그린다. 루트는 `Group`이고 자식은 **몸통 1 + 방향 표식 `rotSymmetry`장**이다.
 * 이 자식 구성은 계약이다 - 고스트(gridHelper)와 레이캐스트 역추적이 같은 구조를 전제한다.
 *
 * 지오메트리와 머티리얼은 전부 `meshFactory`의 공유 자원이며,
 * 인스턴스마다 새로 할당되는 것은 `Mesh` 껍데기와 `Group`뿐이다(500개 배치 전제).
 */
export function createObjectMesh(obj: ObjectInstance, entry: CatalogEntry): Object3D {
  const root = new Group()
  root.name = `object:${obj.id}`
  root.userData[USER_DATA_INSTANCE_ID] = obj.id
  root.userData[USER_DATA_CATALOG_ID] = obj.catalogId

  const body = createObjectBody(entry, obj.colorId)
  body.userData[USER_DATA_ROLE] = ROLE_BODY
  root.add(body)

  const fit = computeMarkerFit(body, entry)

  for (const placement of computeMarkerPlacements(entry.footprint, entry.rotSymmetry)) {
    const marker = createSharedMesh('box', 'marker', obj.colorId)
    marker.position.set(
      placement.offset.x * fit.horizontal,
      fit.bandCenterY,
      placement.offset.z * fit.horizontal,
    )
    marker.scale.set(
      placement.size.x * fit.horizontal,
      placement.size.y * fit.vertical,
      placement.size.z * fit.horizontal,
    )
    marker.userData[USER_DATA_ROLE] = ROLE_MARKER
    root.add(marker)
  }

  applyShadowFlags(root, entry)
  applyTransform(root, obj, entry)
  root.userData[USER_DATA_INSTANCE] = obj

  return root
}

/**
 * 표식을 **점유 셀이 아니라 실제 몸통**에 맞추기 위한 축소율.
 *
 * `computeMarkerPlacements()`는 footprint 기준이다. 그런데 GLB는 에셋의 실제 치수를 유지하므로
 * 머그컵(9.6cm)이 25cm 셀 안에 있는 식으로 몸통이 셀보다 작은 경우가 흔하다.
 * 그대로 두면 표식이 셀 경계, 즉 **몸통에서 7cm 떨어진 허공**에 뜬다.
 * 틀린그림찾기에서 정체불명의 색 조각이 떠 있는 것은 노이즈를 넘어 오답 유도다.
 *
 * 수평은 x·z 중 작은 비율 하나로 묶는다. 축마다 다른 비율을 쓰면 표식 배열의 회전 대칭이
 * 깨져서, 4.2절이 세워 둔 "표식 배열의 대칭 차수 = rotSymmetry" 등식이 무너진다.
 */
function computeMarkerFit(
  body: Object3D,
  entry: CatalogEntry,
): { horizontal: number; vertical: number; bandCenterY: number } {
  const meters = footprintToMeters(entry.footprint)
  const extent = measureBodyExtent(body)

  // 몸통이 비었을 리는 없지만(레시피가 파트를 보장한다), 0으로 나누는 경로는 닫아 둔다.
  if (extent.size.x <= 0 || extent.size.y <= 0 || extent.size.z <= 0) {
    return { horizontal: 1, vertical: 1, bandCenterY: -meters.y / HALF }
  }

  const horizontal = Math.min(1, extent.size.x / meters.x, extent.size.z / meters.z)
  const vertical = Math.min(1, extent.size.y / meters.y)
  const bandHeight = meters.y * MARKER_BAND_RATIO * vertical

  return {
    horizontal,
    vertical,
    bandCenterY: extent.min.y + bandHeight / HALF + extent.size.y * MARKER_BAND_LIFT_RATIO,
  }
}

/** 위치·회전을 상태에 맞춘다. 렌더 객체이므로 제자리 변경이 허용된 지점이다. */
function applyTransform(root: Object3D, obj: ObjectInstance, entry: CatalogEntry): void {
  const center = computeObjectCenterWorld(obj.cell, entry, obj.rot)
  root.position.set(center.x, center.y, center.z)
  root.rotation.y = rotToRadians(obj.rot)
}

/** 큰 가구만 그림자를 드리운다. 나머지는 받기만 한다 - 이유는 상수 주석 참고. */
function applyShadowFlags(root: Object3D, entry: CatalogEntry): void {
  const longestAxis = Math.max(entry.footprint.x, entry.footprint.y, entry.footprint.z)
  const casts = longestAxis >= SHADOW_CASTER_MIN_CELLS

  root.traverse((node) => {
    if (!(node instanceof Mesh)) return
    // 판정 전용 상자는 렌더 목록에 없지만 섀도맵은 머티리얼 가시성을 보지 않는다.
    // 그냥 두면 보이지 않는 상자가 그림자를 드리워 방 전체가 네모 그림자로 덮인다.
    if (isHitCollider(node)) {
      node.castShadow = false
      node.receiveShadow = false
      return
    }
    node.castShadow = casts
    node.receiveShadow = true
  })
}

/**
 * 몸통과 표식의 색을 상태에 맞춘다.
 *
 * 공유 머티리얼을 **제자리 변경하지 않고 참조를 교체**한다. 제자리 변경하면 같은
 * (슬롯, colorId) 조합을 쓰는 다른 오브젝트까지 전부 같이 바뀌어, 한 물건만 바뀌어야 하는
 * 색 변경 동사(5.1절)가 방 전체 변화가 된다. 범위 밖 `colorId`는 팔레트가 던진다.
 */
function applyColors(root: Object3D, colorId: PaletteIndex): void {
  for (const child of root.children) {
    if (readStringUserData(child, USER_DATA_ROLE) === ROLE_MARKER) {
      if (child instanceof Mesh) child.material = getSlotMaterial('marker', colorId)
      continue
    }
    applyBodyColor(child, colorId)
  }
}

/**
 * 노드의 머티리얼 색을 바꾼다. 색을 가진 머티리얼이 없으면 아무것도 하지 않는다 -
 * 오류를 삼키는 것이 아니라 "해당 없음"이 이 함수의 계약이다(그리드 라인 등 비대상 노드 존재).
 *
 * 주의: **공유 머티리얼에 쓰면 안 된다.** 고스트·아웃라인처럼 자기 머티리얼을 가진
 * 노드 전용이다(gridHelper). 오브젝트 뷰의 색 반영은 `applyColors`가 참조 교체로 처리한다.
 */
export function setNodeColor(node: Object3D, color: Color): void {
  if (!isRenderableNode(node)) return

  for (const material of toMaterialList(node.material)) {
    if (!('color' in material)) continue
    const colored = material as Material & { color: Color }
    colored.color.copy(color)
  }
}

/* --- 상태 → 씬 동기화 --------------------------------------------------------- */

/**
 * `RoomState`를 씬에 반영한다. **추가·제거·갱신만** 처리하고 전체 재생성은 하지 않는다.
 *
 * 전체 재생성을 금지하는 이유는 성능만이 아니다. 꾸미기 1분·수색 페이즈 내내
 * 매 상태 변경마다 메시를 새로 만들면 GC 스파이크가 프레임을 끊고,
 * 그 끊김이 곧 "방금 뭐가 바뀐 거지?"라는 판정 불신으로 이어진다.
 * 오브젝트가 400개인 방에서는 이 차이가 체감이 아니라 생존 조건이다.
 *
 * 카탈로그에 없는 `catalogId`를 만나면 **던진다.** room.ts `buildOccupancy`와 같은 태도다 -
 * 조용히 건너뛰면 상태에는 있는데 화면에는 없는 오브젝트가 생기고,
 * 그건 술래가 원리적으로 찾을 수 없는 변화(원칙 2 위반)가 된다.
 */
export function syncObjectViews(ctx: SceneCtx, state: RoomState, catalog: Catalog): void {
  const root = resolveViewRoot(ctx.scene)
  const staleViews = indexViewsByInstanceId(root)

  for (const obj of state.objects.values()) {
    const entry = catalog.get(obj.catalogId)
    if (entry === undefined) {
      throw new Error(
        `syncObjectViews: 카탈로그에 없는 catalogId '${obj.catalogId}' (인스턴스 '${obj.id}')`,
      )
    }

    const view = staleViews.get(obj.id)
    if (view === undefined) {
      root.add(createObjectMesh(obj, entry))
      continue
    }

    // 상태에 남아 있으므로 제거 대상이 아니다.
    staleViews.delete(obj.id)
    updateObjectView(root, view, obj, entry)
  }

  // 여기까지 남은 뷰 = 상태에서 사라진 오브젝트(제거 동사 또는 라운드 전환).
  for (const orphan of staleViews.values()) {
    root.remove(orphan)
    disposeObject3D(orphan)
  }
}

/**
 * 기존 뷰를 상태에 맞게 갱신한다.
 *
 * 첫 검사가 `catalogId`인 이유: `ObjectPatch`에 `catalogId`가 없으므로 정상 경로에서는 바뀔 수 없지만,
 * 만약 어긋난 채 통과시키면 실루엣과 상태가 영구히 불일치한 오브젝트가 남는다.
 * 그때는 갱신이 아니라 재생성만이 답이다.
 */
function updateObjectView(
  root: Object3D,
  view: Object3D,
  obj: ObjectInstance,
  entry: CatalogEntry,
): void {
  if (readStringUserData(view, USER_DATA_CATALOG_ID) !== obj.catalogId) {
    root.remove(view)
    disposeObject3D(view)
    root.add(createObjectMesh(obj, entry))
    return
  }

  // 코어 상태가 불변이므로 참조가 같으면 필드도 같다 - 불변 패턴이 diff 비용을 참조 비교 하나로 줄여 준다.
  if (view.userData[USER_DATA_INSTANCE] === obj) return

  applyTransform(view, obj, entry)
  applyColors(view, obj.colorId)
  view.userData[USER_DATA_INSTANCE] = obj
}

/** 오브젝트 뷰 컨테이너를 찾거나 만든다. 씬에 뷰가 흩어지면 diff 대상 집합이 흐려진다. */
function resolveViewRoot(scene: Object3D): Object3D {
  const existing = scene.getObjectByName(OBJECT_VIEW_ROOT_NAME)
  if (existing !== undefined) return existing

  const created = new Group()
  created.name = OBJECT_VIEW_ROOT_NAME
  scene.add(created)
  return created
}

/** 컨테이너의 직계 자식을 InstanceId로 색인한다. 반환된 맵은 호출부가 소비하며 파괴적으로 쓴다. */
function indexViewsByInstanceId(root: Object3D): Map<InstanceId, Object3D> {
  const views = new Map<InstanceId, Object3D>()

  for (const child of root.children) {
    const instanceId = readStringUserData(child, USER_DATA_INSTANCE_ID)
    if (instanceId === undefined) continue
    views.set(instanceId, child)
  }

  return views
}

/* --- 레이캐스트 역추적 · 자원 해제 --------------------------------------------- */

/**
 * 레이캐스트가 맞힌 Object3D에서 InstanceId를 역추적한다.
 *
 * 부모를 거슬러 올라가는 이유: 광선은 몸통 루트가 아니라 **부품 메시나 방향 표식**에 맞는데,
 * 거기서 undefined를 돌려주면 "다리를 클릭하면 판정이 안 되는" 버그가 된다.
 * 6.1절의 오브젝트 히트가 부품 면적만큼 구멍 나는 셈이라 판정 신뢰도에 직결된다.
 */
export function getInstanceIdFromObject3D(o: Object3D): InstanceId | undefined {
  let current: Object3D | null = o

  while (current !== null) {
    const instanceId = readStringUserData(current, USER_DATA_INSTANCE_ID)
    if (instanceId !== undefined) return instanceId
    current = current.parent
  }

  return undefined
}

/**
 * 하위 트리의 지오메트리·머티리얼을 해제한다. **공유 자원은 건너뛴다.**
 *
 * Three.js는 GPU 자원을 GC 하지 않으므로 제거 시 이걸 부르지 않으면 VRAM이 누적된다.
 * 반대로 공유 자원까지 해제하면 훨씬 나쁜 일이 일어난다 - 소품 하나를 지웠는데
 * 같은 단위 박스를 쓰던 방 전체가 빈 메시가 된다. 그래서 `meshFactory`가 붙여 둔
 * 공유 표식을 보고 걸러낸다. 공유 자원의 해제는 `disposeMeshFactory()`가 통째로 담당한다.
 */
export function disposeObject3D(root: Object3D): void {
  root.traverse((node) => {
    if (!isRenderableNode(node)) return
    if (isSharedResource(node)) return

    node.geometry.dispose()
    for (const material of toMaterialList(node.material)) {
      material.dispose()
    }
  })
}

/** 지오메트리·머티리얼을 가진 노드인가. 이 파일이 만드는 것은 Mesh와 (고스트의) Line뿐이다. */
function isRenderableNode(node: Object3D): node is Mesh | Line {
  return node instanceof Mesh || node instanceof Line
}

function toMaterialList(material: Material | Material[]): readonly Material[] {
  return Array.isArray(material) ? material : [material]
}

/** userData는 임의 값이 들어갈 수 있으므로 경계에서 타입을 확인한다. */
function readStringUserData(target: Object3D, key: string): string | undefined {
  const value: unknown = target.userData[key]
  return typeof value === 'string' ? value : undefined
}
